import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { TFunction } from 'i18next'
import { formatNumberUsage } from '@/utils/function'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { VCloudRow } from '../Information/vcloud-config'

// ============================================================================
// Helpers (same as Information)
// ============================================================================

const parseVMConfig = (name: string) => {
    if (!name) return { cores: 0, ram: 0 }
    const match = name.match(/(\d+)-(\d+)$/)
    if (match) {
        return {
            cores: parseInt(match[1]),
            ram: parseInt(match[2]),
        }
    }
    return { cores: 0, ram: 0 }
}

// ============================================================================
// Column Visibility Type
// ============================================================================

interface VCloudColumnVisibility {
    hasPublicIP?: boolean
    hasFloatingIP?: boolean
    hasBandwidth?: boolean
}

// ============================================================================
// Setting Columns (subset of Information columns)
// ============================================================================

interface CreateVCloudSettingColumnsOptions {
    t: TFunction
    expandedServices: Set<string>
    onToggleExpand: (serviceId: string) => void
    columnVisibility?: VCloudColumnVisibility
}

/**
 * Create Setting-specific columns
 * - Same structure as Information (Service -> Type -> Items)
 * - Shows: name, vCores, RAM, Storage, IP, Bandwidth, Price
 * - Does NOT show: employee_names, dates, status, contract
 */
export const createVCloudSettingColumns = ({
    t,
    expandedServices,
    onToggleExpand,
    columnVisibility,
}: CreateVCloudSettingColumnsOptions): DataTableColumn<VCloudRow>[] => {
    const columns: DataTableColumn<VCloudRow>[] = [
        // Service name column with expand/collapse
        {
            key: 'service_name',
            title: t('service_name'),
            width: 320,
            pinned: 'left',
            render: (_, row) => {
                // Service header - show service name with expand/collapse
                if (row._rowType === 'service-header') {
                    const isExpanded =
                        expandedServices?.has(row.service_id || '') ?? false
                    const isMain = row._isMainService

                    return (
                        <div
                            className="flex items-center gap-1 cursor-pointer select-none"
                            onClick={() => onToggleExpand(row.service_id || '')}
                        >
                            {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            {!isMain && (
                                <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded mr-1 uppercase">
                                    Addon
                                </span>
                            )}
                            <span>{row.name}</span>
                            {!isMain && (
                                <span className="text-xs text-muted-foreground ml-1">
                                    ({row.service_id})
                                </span>
                            )}
                        </div>
                    )
                }
                // Type header - show type name with indent
                if (row._rowType === 'type-header') {
                    return (
                        <span className="pl-6 uppercase text-muted-foreground text-xs">
                            {row.name}
                        </span>
                    )
                }
                // Summary row - show total label
                if (row._rowType === 'summary') {
                    return row.name
                }
                // Item row - show service name with quantity (indented)
                const qty =
                    row.commitment_value && row.commitment_value > 1
                        ? ` (x${row.commitment_value})`
                        : ''
                return (
                    <span className="pl-10">
                        {t(`${row.name}`)}
                        {qty}
                    </span>
                )
            },
        },
        // vCores column
        {
            key: 'vcores',
            title: 'vCores',
            width: 80,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalVCores && row.totalVCores > 0
                        ? formatNumberUsage(row.totalVCores)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalVCores && row.totalVCores > 0
                        ? formatNumberUsage(row.totalVCores)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType === 'other_service') return null
                    return row.totalVCores && row.totalVCores > 0
                        ? formatNumberUsage(row.totalVCores)
                        : null
                }
                if (row._serviceType === 'other_service') return null
                const config = parseVMConfig(row.name || '')
                return config.cores > 0 ? formatNumberUsage(config.cores) : null
            },
        },
        // RAM column
        {
            key: 'ram',
            title: 'RAM',
            width: 80,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalRAM && row.totalRAM > 0
                        ? formatNumberUsage(row.totalRAM)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalRAM && row.totalRAM > 0
                        ? formatNumberUsage(row.totalRAM)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType === 'other_service') return null
                    return row.totalRAM && row.totalRAM > 0
                        ? formatNumberUsage(row.totalRAM)
                        : null
                }
                if (row._serviceType === 'other_service') return null
                const config = parseVMConfig(row.name || '')
                return config.ram > 0 ? formatNumberUsage(config.ram) : null
            },
        },
        // Storage column
        {
            key: 'storage',
            title: 'Storage',
            width: 100,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalStorage && row.totalStorage > 0
                        ? formatNumberUsage(row.totalStorage)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalStorage && row.totalStorage > 0
                        ? formatNumberUsage(row.totalStorage)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalStorage && row.totalStorage > 0
                        ? formatNumberUsage(row.totalStorage)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.storage && row.storage > 0
                    ? formatNumberUsage(row.storage)
                    : null
            },
        },
    ]

    // Public IP column - only if has data
    if (columnVisibility?.hasPublicIP) {
        columns.push({
            key: 'publicIP',
            title: 'Public IP',
            width: 90,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalPublicIP && row.totalPublicIP > 0
                        ? formatNumberUsage(row.totalPublicIP)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalPublicIP && row.totalPublicIP > 0
                        ? formatNumberUsage(row.totalPublicIP)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalPublicIP && row.totalPublicIP > 0
                        ? formatNumberUsage(row.totalPublicIP)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.publicIP && row.publicIP > 0
                    ? formatNumberUsage(row.publicIP)
                    : null
            },
        })
    }

    // Floating IP column - only if has data
    if (columnVisibility?.hasFloatingIP) {
        columns.push({
            key: 'floatingIP',
            title: 'Floating IP',
            width: 100,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalFloatingIP && row.totalFloatingIP > 0
                        ? formatNumberUsage(row.totalFloatingIP)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalFloatingIP && row.totalFloatingIP > 0
                        ? formatNumberUsage(row.totalFloatingIP)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalFloatingIP && row.totalFloatingIP > 0
                        ? formatNumberUsage(row.totalFloatingIP)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.floatingIP && row.floatingIP > 0
                    ? formatNumberUsage(row.floatingIP)
                    : null
            },
        })
    }

    // Bandwidth column - only if has data
    if (columnVisibility?.hasBandwidth) {
        columns.push({
            key: 'bandwidth',
            title: 'Bandwidth',
            width: 100,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalBandwidth && row.totalBandwidth > 0
                        ? formatNumberUsage(row.totalBandwidth)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalBandwidth && row.totalBandwidth > 0
                        ? formatNumberUsage(row.totalBandwidth)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalBandwidth && row.totalBandwidth > 0
                        ? formatNumberUsage(row.totalBandwidth)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.bandwidth && row.bandwidth > 0
                    ? formatNumberUsage(row.bandwidth)
                    : null
            },
        })
    }

    // Price columns
    columns.push(
        {
            key: 'commitment_price_per_unit',
            title: t('unit_price', { defaultValue: 'Đơn giá' }),
            width: 130,
            cellClassName: 'text-right',
            render: (value, row) => {
                // Only show for bundle pricing on item rows
                if (row.service_pricing_model === 'package') return null
                if (row._rowType !== 'item') return null
                return value
                    ? new Intl.NumberFormat('en-US').format(value)
                    : null
            },
        },
        {
            key: 'commitment_price_total',
            title: t('total_price', { defaultValue: 'Thành tiền' }),
            width: 130,
            cellClassName: 'text-right',
            render: (_, row) => {
                const isPackageRow = row.service_pricing_model === 'package'

                if (isPackageRow) {
                    // Package: show service_price_register on service-header and summary only
                    if (
                        row._rowType !== 'summary' &&
                        row._rowType !== 'service-header'
                    )
                        return null
                    return new Intl.NumberFormat('en-US').format(
                        row.service_price_register ?? 0
                    )
                } else {
                    // Bundle: show commitment_price_total
                    if (row._rowType === 'type-header') return null
                    if (
                        (row._rowType === 'service-header' ||
                            row._rowType === 'summary') &&
                        row.commitment_price_total
                    ) {
                        return new Intl.NumberFormat('en-US').format(
                            row.commitment_price_total
                        )
                    }
                    return row.commitment_price_total
                        ? new Intl.NumberFormat('en-US').format(
                              row.commitment_price_total
                          )
                        : null
                }
            },
        }
    )

    return columns
}
